import { createResource, type RouteResource } from 'react-resource-router';

import type { DocNode } from '@atlaskit/adf-schema';
import { fg } from '@atlaskit/platform-feature-flags';
import type { Group } from '@atlassian/aiops-group-common';
import { groupCustomizationFeatureStateResource } from '@atlassian/aiops-group-common/resources';
import { assert, AssertionError } from '@atlassian/eoc-common';

import type { AISuggestions } from '../../common/types';
import { getAISuggestions, getCorrelationSummary, getHeaderDetails } from '../endpoint';

export const groupHeaderResource: RouteResource<Group> = createResource({
	type: 'groupHeaderResource',
	getKey: (routerContext) => `groupHeaderResource#${routerContext.match.params.id}`,
	getData: async (routerContext) => {
		const groupId = routerContext.match.params?.id;

		assert(
			!!groupId,
			new AssertionError(
				'groupHeaderResource',
				'groupId should be set to fetch group-detail-header',
			),
		);

		return getHeaderDetails(groupId);
	},
	isBrowserOnly: true,
});

export const correlationSummaryResource: RouteResource<{ summary: DocNode }> = createResource({
	type: 'correlationSummaryResource',
	getKey: (routerContext) => `correlationSummaryResource#${routerContext.match.params.id}`,
	getData: async (routerContext) => {
		const groupId = routerContext.match.params?.id;

		assert(
			!!groupId,
			new AssertionError(
				'correlationSummaryResource',
				'groupId should be set to fetch correlation summary',
			),
		);

		const adfJson = await getCorrelationSummary(groupId);
		return { summary: JSON.parse(adfJson) as DocNode };
	},
	isBrowserOnly: true,
});

export const aiSuggestionsResource: RouteResource<AISuggestions> = createResource({
	type: 'aiSuggestionsResource',
	getKey: (routerContext) => `aiSuggestionsResource#${routerContext.match.params.id}`,
	getData: async (routerContext) => {
		const groupId = routerContext.match.params?.id;

		assert(
			!!groupId,
			new AssertionError(
				'aiSuggestionsResource',
				'groupId should be set to fetch ai-suggestions for any group',
			),
		);

		return getAISuggestions(groupId);
	},
	isBrowserOnly: true,
});

export const resources = (): (
	| RouteResource<Group>
	| RouteResource<AISuggestions>
	| RouteResource<{ summary: DocNode }>
	| RouteResource<{
			isEnabled: boolean;
	  }>
)[] => [
	groupHeaderResource,
	aiSuggestionsResource,
	groupCustomizationFeatureStateResource,
	...(fg('aiops-alert-correlation') ? [correlationSummaryResource] : []),
];
